import { ServicioAdicional } from "../../entities/catalogos/ServicioAdicional";
import { IDocumento } from "../../interfaces/IDocumento";
import { enums } from "../../enums/enums";


const IDS_AUTORIDAD_E3 = new Set<number>([
    9,  // CARGO POR CITA RAD
    47, // FLETE CARGO RAD FUERA DE HORARIO
    6,  // FLETE CARGO EAD FUERA DE HORARIO
]);

const ID_AUTORIDAD_DESTINO = 12; // CARGO POR CITA EAD

const ID_RETORNO_DOCUMENTOS = enums.servicioAdicional.retornoDocumentos;

export type ServiciosGuiaConsolidados = {
    servicios: ServicioAdicional[];
    documentosRetorno: IDocumento[];
};

type SilosGuia = {
    origen: ServicioAdicional[];
    destino: ServicioAdicional[];
    e3: ServicioAdicional[];
};

type AutoridadSeleccion = "e3" | "destino" | null;

const asLista = (servicios: ServicioAdicional[] | undefined | null): ServicioAdicional[] =>
    Array.isArray(servicios) ? servicios : [];

const existeIdEn = (lista: ServicioAdicional[], id: number): boolean =>
    lista.some(s => s.id === id);

const encontrarPorId = (lista: ServicioAdicional[], id: number): ServicioAdicional | undefined =>
    lista.find(s => s.id === id);

const clonarDocumentos = (documentos: IDocumento[] | undefined | null): IDocumento[] =>
    (documentos ?? []).map(d => ({ ...d }));

const valorPresente = <T>(valor: T | null | undefined): valor is T =>
    valor !== null && valor !== undefined;

const autoridadSeleccion = (id: number, silos: SilosGuia): AutoridadSeleccion => {
    if (IDS_AUTORIDAD_E3.has(id) && existeIdEn(silos.e3, id)) {
        return "e3";
    }
    if (id === ID_AUTORIDAD_DESTINO && existeIdEn(silos.destino, id)) {
        return "destino";
    }
    return null;
};

const seleccionCanonica = (id: number, silos: SilosGuia): boolean => {
    const autoridad = autoridadSeleccion(id, silos);
    if (autoridad === "e3") {
        return encontrarPorId(silos.e3, id)?.seleccionado === true;
    }
    if (autoridad === "destino") {
        return encontrarPorId(silos.destino, id)?.seleccionado === true;
    }
    return (
        silos.origen.some(s => s.id === id && s.seleccionado === true)
        || silos.destino.some(s => s.id === id && s.seleccionado === true)
        || silos.e3.some(s => s.id === id && s.seleccionado === true)
    );
};

const ejemplaresFuncionales = (id: number, silos: SilosGuia): ServicioAdicional[] => {
    const autoridad = autoridadSeleccion(id, silos);
    if (autoridad === "e3") {
        const e3 = encontrarPorId(silos.e3, id);
        return e3?.seleccionado === true ? [e3] : [];
    }
    if (autoridad === "destino") {
        const destino = encontrarPorId(silos.destino, id);
        return destino?.seleccionado === true ? [destino] : [];
    }
    const funcionales: ServicioAdicional[] = [];
    silos.origen.forEach(s => {
        if (s.id === id && s.seleccionado === true) {
            funcionales.push(s);
        }
    });
    silos.destino.forEach(s => {
        if (s.id === id && s.seleccionado === true) {
            funcionales.push(s);
        }
    });
    silos.e3.forEach(s => {
        if (s.id === id && s.seleccionado === true) {
            funcionales.push(s);
        }
    });
    return funcionales;
};

const entreFuncionalesPorPrioridadSilo = (
    id: number,
    funcionales: ServicioAdicional[],
    silos: SilosGuia,
    pred: (s: ServicioAdicional) => boolean
): ServicioAdicional | undefined => {
    const ordenSilos = [silos.e3, silos.destino, silos.origen];
    for (const lista of ordenSilos) {
        const delSilo = lista.find(s => s.id === id);
        if (delSilo && funcionales.indexOf(delSilo) >= 0 && pred(delSilo)) {
            return delSilo;
        }
    }
    return undefined;
};

const pickCosto = (
    id: number,
    funcionales: ServicioAdicional[],
    silos: SilosGuia
): number | null | undefined => {
    const conCosto = entreFuncionalesPorPrioridadSilo(
        id,
        funcionales,
        silos,
        s => valorPresente(s.costo)
    );
    return conCosto?.costo;
};

const pickCantidadOrigen = (
    id: number,
    funcionales: ServicioAdicional[],
    silos: SilosGuia
): number | null | undefined => {
    const fuente = entreFuncionalesPorPrioridadSilo(
        id,
        funcionales,
        silos,
        s => s.aplicaOrigen === true && valorPresente(s.cantidadOrigen)
    );
    return fuente?.cantidadOrigen;
};

const pickCantidadDestino = (
    id: number,
    funcionales: ServicioAdicional[],
    silos: SilosGuia
): number | null | undefined => {
    const fuente = entreFuncionalesPorPrioridadSilo(
        id,
        funcionales,
        silos,
        s => s.aplicaDestino === true && valorPresente(s.cantidadDestino)
    );
    return fuente?.cantidadDestino;
};

const pickCantidad = (
    id: number,
    funcionales: ServicioAdicional[],
    silos: SilosGuia
): number | null | undefined => {
    const fuente = entreFuncionalesPorPrioridadSilo(
        id,
        funcionales,
        silos,
        s => valorPresente(s.cantidad)
    );
    return fuente?.cantidad;
};

const pickDocumentos = (
    id: number,
    funcionales: ServicioAdicional[],
    silos: SilosGuia
): IDocumento[] | undefined => {
    if (id === ID_RETORNO_DOCUMENTOS) {
        const e3 = encontrarPorId(silos.e3, id);
        if (e3?.seleccionado === true && e3.documentos && e3.documentos.length > 0) {
            return clonarDocumentos(e3.documentos);
        }
        return undefined;
    }
    const fuente = entreFuncionalesPorPrioridadSilo(
        id,
        funcionales,
        silos,
        s => Array.isArray(s.documentos) && s.documentos.length > 0
    );
    return fuente ? clonarDocumentos(fuente.documentos) : undefined;
};

type CampoMetadato =
    | "nombre"
    | "configuracion"
    | "categoriaId"
    | "tipoCostoID"
    | "etiquetaServicioAdicionalDto"
    | "concepto"
    | "abierto"
    | "esAbierto"
    | "esOrigen"
    | "esDestino"
    | "esSeccionOrigenDestino"
    | "esSeccionServiciosAdicionales"
    | "esCantidad"
    | "esRetornoDocumentos"
    | "esRequerido"
    | "convenioId"
    | "tieneConvenio"
    | "mostrarCheckOrigen"
    | "mostrarCheckDestino"
    | "mostrarCantidadOrigen"
    | "mostrarCantidadDestino"
    | "destinoSeleccionado"
    | "abrirDialog"
    | "seleccionDirecta";

const CAMPOS_METADATO: CampoMetadato[] = [
    "nombre",
    "configuracion",
    "categoriaId",
    "tipoCostoID",
    "etiquetaServicioAdicionalDto",
    "concepto",
    "abierto",
    "esAbierto",
    "esOrigen",
    "esDestino",
    "esSeccionOrigenDestino",
    "esSeccionServiciosAdicionales",
    "esCantidad",
    "esRetornoDocumentos",
    "esRequerido",
    "convenioId",
    "tieneConvenio",
    "mostrarCheckOrigen",
    "mostrarCheckDestino",
    "mostrarCantidadOrigen",
    "mostrarCantidadDestino",
    "destinoSeleccionado",
    "abrirDialog",
    "seleccionDirecta",
];

const completarMetadatos = (id: number, silos: SilosGuia): Partial<ServicioAdicional> => {
    const cadena = [
        encontrarPorId(silos.e3, id),
        encontrarPorId(silos.destino, id),
        encontrarPorId(silos.origen, id),
    ];
    const meta: Partial<ServicioAdicional> = { id };
    CAMPOS_METADATO.forEach(campo => {
        for (const ejemplar of cadena) {
            if (!ejemplar) {
                continue;
            }
            const valor = (ejemplar as any)[campo];
            if (valorPresente(valor)) {
                (meta as any)[campo] = valor;
                break;
            }
        }
    });
    return meta;
};

const idsUnicos = (silos: SilosGuia): number[] => {
    const vistos = new Set<number>();
    const orden: number[] = [];
    const registrar = (lista: ServicioAdicional[]) => {
        lista.forEach(s => {
            if (!vistos.has(s.id)) {
                vistos.add(s.id);
                orden.push(s.id);
            }
        });
    };
    registrar(silos.origen);
    registrar(silos.destino);
    registrar(silos.e3);
    return orden;
};

const consolidarConcepto = (id: number, silos: SilosGuia): ServicioAdicional | null => {
    if (!seleccionCanonica(id, silos)) {
        return null;
    }

    const funcionales = ejemplaresFuncionales(id, silos);
    if (funcionales.length === 0) {
        return null;
    }

    const metadatos = completarMetadatos(id, silos);
    const aplicaOrigen = funcionales.some(s => s.aplicaOrigen === true);
    const aplicaDestino = funcionales.some(s => s.aplicaDestino === true);
    const documentos = pickDocumentos(id, funcionales, silos);
    const costo = pickCosto(id, funcionales, silos);
    const cantidadOrigen = pickCantidadOrigen(id, funcionales, silos);
    const cantidadDestino = pickCantidadDestino(id, funcionales, silos);
    const cantidad = pickCantidad(id, funcionales, silos);

    const props = {
        ...metadatos,
        id,
        seleccionado: true,
        aplicaOrigen,
        aplicaDestino,
        cantidadOrigen: valorPresente(cantidadOrigen) ? cantidadOrigen : null,
        cantidadDestino: valorPresente(cantidadDestino) ? cantidadDestino : null,
        cantidad: valorPresente(cantidad) ? cantidad : null,
        costo: valorPresente(costo) ? costo : null,
        documentos,
        esRetornoDocumentos:
            metadatos.esRetornoDocumentos === true || id === ID_RETORNO_DOCUMENTOS,
    } as ServicioAdicional;

    const consolidado = new ServicioAdicional(props);
    if (valorPresente(metadatos.convenioId)) {
        consolidado.convenioId = metadatos.convenioId as number;
    }
    if (valorPresente(metadatos.tieneConvenio)) {
        consolidado.tieneConvenio = metadatos.tieneConvenio as boolean;
    }
    if (documentos) {
        consolidado.documentos = documentos;
    }
    consolidado.seleccionado = true;
    consolidado.aplicaOrigen = aplicaOrigen;
    consolidado.aplicaDestino = aplicaDestino;
    consolidado.cantidadOrigen = valorPresente(cantidadOrigen) ? cantidadOrigen : null;
    consolidado.cantidadDestino = valorPresente(cantidadDestino) ? cantidadDestino : null;
    consolidado.cantidad = valorPresente(cantidad) ? cantidad : null;
    consolidado.costo = valorPresente(costo) ? costo : null;

    return consolidado;
};

export const consolidarServiciosGuia = (
    serviciosOrigen: ServicioAdicional[] | undefined | null,
    serviciosDestino: ServicioAdicional[] | undefined | null,
    serviciosE3: ServicioAdicional[] | undefined | null
): ServiciosGuiaConsolidados => {
    const silos: SilosGuia = {
        origen: asLista(serviciosOrigen),
        destino: asLista(serviciosDestino),
        e3: asLista(serviciosE3),
    };

    const servicios: ServicioAdicional[] = [];
    idsUnicos(silos).forEach(id => {
        const concepto = consolidarConcepto(id, silos);
        if (concepto) {
            servicios.push(concepto);
        }
    });

    const retorno = servicios.find(s => s.id === ID_RETORNO_DOCUMENTOS && s.seleccionado);
    const documentosRetorno = retorno?.documentos ? clonarDocumentos(retorno.documentos) : [];

    return { servicios, documentosRetorno };
};
